/**
 * @file useNotFound.ts
 * @description Журнал «Сайт → SEO → Не найдено» (phase19.16, партия
 * «SEO-техника», v0.19.65; SB148; db-design-seo-tech §4.2): период
 * («Сегодня · 7 дней · 30 дней», по умолчанию 7 дней), поиск по адресу (с
 * задержкой, на сервере), фильтр «Обработанные и скрытые», порядок,
 * постранично по 50 («Показать ещё»), «Скрыть» и «Вернуть в список».
 *
 * Один источник правды — ответ сервера: после «Скрыть», «Вернуть» и
 * добавления переадресации журнал перечитывается, а не правится руками.
 * Счётчик вкладки «Не найдено (N)» — необработанные нескрытые адреса за 7
 * дней: берётся из ответа на запрос по умолчанию, а при другом фильтре —
 * отдельным коротким запросом (`limit=1`) после каждого изменения.
 * Хранилище журнала недоступно (503) — признак `unavailable`, свой текст.
 */

import { NOT_FOUND_DEFAULT_PERIOD, type NotFoundJournalDto, type NotFoundJournalRowDto, type NotFoundPeriod, type NotFoundSort } from "@chaiberi/shared";
import { useCallback, useEffect, useRef, useState } from "react";

import { ApiError } from "../../lib/api-client";
import { NOT_FOUND_PAGE, siteNotFound, type NotFoundQuery } from "../../lib/api-site-not-found";
import { NOT_FOUND_DEFAULT_SORT } from "../../lib/site-seo/not-found-view";
import { seoErrorText } from "../../lib/site-seo/seo-errors";

const SEARCH_DELAY_MS = 300;

export interface NotFoundApi {
  journal: NotFoundJournalDto | null;
  rows: NotFoundJournalRowDto[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  /** Хранилище журнала недоступно (503) — «Журнал временно недоступен». */
  unavailable: boolean;
  /** Необработанные нескрытые адреса за 7 дней — счётчик вкладки. */
  count: number | undefined;
  period: NotFoundPeriod;
  setPeriod: (p: NotFoundPeriod) => void;
  query: string;
  setQuery: (q: string) => void;
  all: boolean;
  setAll: (value: boolean) => void;
  sort: NotFoundSort;
  setSort: (s: NotFoundSort) => void;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  reload: () => Promise<void>;
  /** Скрыть / вернуть; ошибка пробрасывается вызывающему (тост). */
  hide: (path: string) => Promise<void>;
  unhide: (path: string) => Promise<void>;
}

/** Запрос по умолчанию даёт и счётчик вкладки. */
const isDefaultView = (q: NotFoundQuery): boolean => q.period === NOT_FOUND_DEFAULT_PERIOD && !q.all && !q.q?.trim();

export function useNotFound(): NotFoundApi {
  const [journal, setJournal] = useState<NotFoundJournalDto | null>(null);
  const [rows, setRows] = useState<NotFoundJournalRowDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [count, setCount] = useState<number | undefined>(undefined);
  const [period, setPeriod] = useState<NotFoundPeriod>(NOT_FOUND_DEFAULT_PERIOD);
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [all, setAll] = useState(false);
  const [sort, setSort] = useState<NotFoundSort>(NOT_FOUND_DEFAULT_SORT);
  // Ответ на устаревший запрос (сменили фильтр, пока грузилось) не кладём.
  const request = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => setSearch(query), SEARCH_DELAY_MS);
    return () => clearTimeout(t);
  }, [query]);

  /** Счётчик вкладки, когда на экране не запрос по умолчанию. */
  const refreshCount = useCallback(async () => {
    try {
      const res = await siteNotFound.list({ period: NOT_FOUND_DEFAULT_PERIOD, all: false, sort: NOT_FOUND_DEFAULT_SORT, limit: 1 });
      setCount(res.total);
    } catch {
      // Счётчик не критичен: вкладка работает и без него.
    }
  }, []);

  const reload = useCallback(async () => {
    const id = ++request.current;
    const q: NotFoundQuery = { period, all, q: search, sort };
    setLoading(true);
    setError(null);
    try {
      const res = await siteNotFound.list(q);
      if (id !== request.current) return;
      setJournal(res);
      setRows(res.rows);
      setUnavailable(false);
      if (isDefaultView(q)) setCount(res.total);
      else void refreshCount();
    } catch (err) {
      if (id !== request.current) return;
      setUnavailable(err instanceof ApiError && err.status === 503);
      setError(seoErrorText(err, "Не удалось загрузить журнал «Не найдено»."));
    } finally {
      if (id === request.current) setLoading(false);
    }
  }, [period, all, search, sort, refreshCount]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const loadMore = useCallback(async () => {
    const id = request.current;
    setLoadingMore(true);
    try {
      const res = await siteNotFound.list({ period, all, q: search, sort, offset: rows.length, limit: NOT_FOUND_PAGE });
      if (id !== request.current) return;
      setJournal(res);
      setRows((prev) => [...prev, ...res.rows]);
    } catch (err) {
      setError(seoErrorText(err, "Не удалось загрузить ещё."));
    } finally {
      setLoadingMore(false);
    }
  }, [period, all, search, sort, rows.length]);

  const hide = useCallback(
    async (path: string) => {
      await siteNotFound.hide(path);
      await reload();
    },
    [reload],
  );
  const unhide = useCallback(
    async (path: string) => {
      await siteNotFound.unhide(path);
      await reload();
    },
    [reload],
  );

  const hasMore = journal !== null && rows.length < journal.total;
  return { journal, rows, loading, loadingMore, error, unavailable, count, period, setPeriod, query, setQuery, all, setAll, sort, setSort, hasMore, loadMore, reload, hide, unhide };
}
